import { useSyncExternalStore } from 'react'

export type Lang = 'zh' | 'en'

const STORAGE_KEY = 'pcba-global-view.lang'

/** [Chinese, English] */
const dict: Record<string, readonly [string, string]> = {
  'app.docTitle': ['PCBA Global View — Gerber 查看与快速报价', 'PCBA Global View — Gerber viewer & instant quote'],
  'app.description': [
    '浏览器内 Gerber 查看与 PCB / PCBA 快速报价。拖入 ZIP，查看整块板子，报价并发送询价。文件不上传。',
    'Inspect Gerber layers in your browser, get an indicative PCB or PCBA price, and send an RFQ. Files never leave your device.',
  ],

  // Rail
  'rail.projects': ['项目', 'Projects'],
  'rail.new': ['新建项目', 'New project'],
  'rail.newFolder': ['选择文件夹', 'Choose folder'],
  'rail.search': ['搜索项目', 'Search projects'],
  'rail.emptyTitle': ['还没有项目', 'No projects yet'],
  'rail.emptyBody': ['拖入 Gerber ZIP，项目会自动保存在本机。', 'Drop a Gerber ZIP. Projects are saved on this device.'],
  'rail.demo': ['示例板', 'Demo board'],
  'rail.demoMeta': ['2 层 · 7 文件', '2L · 7 files'],
  'rail.local': ['仅保存在此浏览器', 'Saved in this browser only'],
  'rail.privacy': ['隐私', 'Privacy'],
  'rail.terms': ['条款', 'Terms'],
  'rail.files': ['文件处理', 'File handling'],
  'rail.filesShort': ['文件处理', 'Files'],
  'rail.collapse': ['收起项目栏', 'Collapse projects'],
  'rail.expand': ['展开项目栏', 'Expand projects'],
  'rail.storage': ['{n} 个项目 · 本机', '{n} projects · local'],

  // Project
  'project.untitled': ['未命名板', 'Untitled board'],
  'project.rename': ['重命名', 'Rename'],
  'project.renamePlaceholder': ['板子名称', 'Board name'],
  'project.delete': ['删除项目', 'Delete project'],
  'project.deleteTitle': ['删除这个项目？', 'Delete this project?'],
  'project.deleteBody': ['本地保存的文件和报价会一起删除，无法恢复。', 'The locally saved files and quote will be deleted permanently.'],
  'project.download': ['下载 SVG', 'Download SVG'],
  'project.more': ['更多操作', 'More actions'],
  'project.justNow': ['刚刚', 'just now'],
  'project.minutesAgo': ['{n} 分钟前', '{n}m ago'],
  'project.hoursAgo': ['{n} 小时前', '{n}h ago'],
  'project.yesterday': ['昨天', 'yesterday'],
  'project.daysAgo': ['{n} 天前', '{n}d ago'],
  'project.saved': ['已保存', 'Saved'],
  'project.saving': ['保存中…', 'Saving…'],
  'project.saveFailed': ['未保存', 'Not saved'],
  'project.savedHint': ['自动保存在本机', 'Autosaved on this device'],

  // Empty state
  'empty.title': ['拖入 Gerber', 'Drop a Gerber'],
  'empty.body': ['ZIP 或 Gerber / Excellon 文件。解析和渲染都在浏览器里完成。', 'ZIP or Gerber / Excellon files. Parsed and rendered in your browser.'],
  'empty.choose': ['选择文件', 'Choose files'],
  'empty.chooseFolder': ['选择文件夹', 'Choose folder'],
  'empty.demo': ['打开示例板', 'Open demo board'],
  'empty.limit': ['ZIP / 多文件 · 最大 25 MB', 'ZIP / multiple files · 25 MB max'],
  'empty.recent': ['最近项目', 'Recent projects'],

  // Drop overlay
  'drop.title': ['松手载入', 'Release to open'],
  'drop.body': ['Gerber ZIP、Gerber / Excellon 或 BOM CSV', 'Gerber ZIP, Gerber / Excellon, or BOM CSV'],

  // Status
  'status.reading': ['读取文件', 'Reading'],
  'status.scanning': ['扫描文件夹，识别项目', 'Scanning folder for projects'],
  'status.importing': ['正在导入 {current}/{total}：{name}', 'Importing {current}/{total}: {name}'],
  'status.unzipping': ['解压', 'Unpacking'],
  'status.detecting': ['识别图层', 'Detecting layers'],
  'status.rendering': ['渲染板子', 'Rendering board'],
  'status.saving': ['保存到本机', 'Saving locally'],
  'status.failed': ['载入失败', 'Load failed'],

  // Canvas
  'canvas.top': ['顶层', 'Top'],
  'canvas.bottom': ['底层', 'Bottom'],
  'canvas.perspective': ['透视', 'Tilt'],
  'canvas.threeD': ['3D', '3D'],
  'canvas.fit': ['适应', 'Fit'],
  'canvas.actual': ['1:1', '1:1'],
  'canvas.layers': ['图层', 'Layers'],
  'canvas.files': ['文件', 'Files'],
  'canvas.download': ['下载 SVG', 'Download SVG'],
  'canvas.fullscreen': ['全屏', 'Fullscreen'],
  'canvas.exitFullscreen': ['退出全屏', 'Exit fullscreen'],
  'canvas.zoomIn': ['放大', 'Zoom in'],
  'canvas.zoomOut': ['缩小', 'Zoom out'],
  'canvas.errorTitle': ['这块板子没能渲染', 'This board could not be rendered'],
  'canvas.retry': ['重试', 'Retry'],
  'canvas.demo': ['打开示例板', 'Open demo'],
  'canvas.local': ['本地渲染', 'LOCAL'],

  // 3D
  'three.badge': ['3D 图层检视', '3D layer inspector'],
  'three.solid': ['实体', 'Solid'],
  'three.explode': ['分解', 'Exploded'],
  'three.loading': ['正在生成 3D 图层…', 'Building 3D layers…'],
  'three.hint': ['拖拽旋转 · 滚轮缩放', 'Drag to orbit · scroll to zoom'],
  'three.reset': ['重置视角', 'Reset view'],
  'three.error': ['3D 生成失败', '3D build failed'],
  'canvas.panel': ['拼板', 'Panel'],
  'canvas.original': ['原始', 'Original'],

  // Layers
  'layers.title': ['图层', 'Layers'],
  'layers.count': ['{n} 个文件', '{n} files'],
  'layers.stack': ['自上而下', 'Top to bottom'],
  'layers.collapse': ['收起图层条', 'Collapse layer rail'],
  'layers.expand': ['展开图层条', 'Expand layer rail'],
  'layers.all': ['全开', 'All on'],
  'layers.none': ['全关', 'All off'],
  'layers.manage': ['管理文件与映射…', 'Manage files & mapping…'],
  'layers.unmapped': ['未识别', 'Unmapped'],
  'layers.toggle': ['显示或隐藏 {name}', 'Toggle {name}'],
  'layers.empty': ['没有图层', 'No layers'],
  'layers.noDrill': ['缺钻孔', 'No drill'],
  'layers.noOutline': ['缺板框', 'No outline'],

  // Stats / checks
  'stats.size': ['尺寸', 'Size'],
  'stats.copper': ['铜层', 'Copper'],
  'stats.drills': ['钻孔', 'Drills'],
  'stats.files': ['文件', 'Files'],
  'stats.minAperture': ['最小孔径', 'Min Ø'],
  'checks.title': ['检查', 'Checks'],
  'checks.ok': ['{n} 项通过', '{n} passed'],
  'checks.missing': ['{n} 项缺失', '{n} missing'],
  'check.topCopper': ['顶层铜', 'Top copper'],
  'check.bottomCopper': ['底层铜', 'Bottom copper'],
  'check.outline': ['板框', 'Outline'],
  'check.drill': ['钻孔', 'Drill'],
  'check.topMask': ['顶层阻焊', 'Top mask'],
  'check.topSilk': ['顶层丝印', 'Top silk'],
  'check.innerCopper': ['内层铜', 'Inner copper'],

  // Quote
  'quote.title': ['报价', 'Quote'],
  'quote.mode.pcb': ['PCB 制板', 'PCB fab'],
  'quote.mode.pcba': ['PCBA 贴片', 'PCBA'],
  'quote.qty': ['数量', 'Quantity'],
  'quote.layers': ['层数', 'Layers'],
  'quote.thickness': ['板厚', 'Thickness'],
  'quote.finish': ['表面', 'Finish'],
  'quote.lead': ['交期', 'Lead'],
  'quote.advanced': ['工艺细节', 'Process details'],
  'quote.copper': ['铜厚', 'Copper weight'],
  'quote.mask': ['阻焊', 'Solder mask'],
  'quote.silk': ['丝印', 'Silkscreen'],
  'quote.trace': ['线宽 / 间距', 'Trace / space'],
  'quote.testing': ['电测', 'E-test'],
  'quote.impedance': ['阻抗控制', 'Impedance'],
  'quote.castellation': ['半孔', 'Castellation'],
  'quote.sides': ['面', 'Sides'],
  'quote.technology': ['工艺', 'Technology'],
  'quote.complexity': ['复杂度', 'Complexity'],
  'quote.assemblyTest': ['测试', 'Test'],
  'quote.coating': ['三防漆', 'Coating'],
  'quote.boxBuild': ['整机组装', 'Box build'],
  'quote.includePcb': ['含裸板', 'Include bare PCB'],
  'quote.bom': ['BOM', 'BOM'],
  'quote.bomDrop': ['拖入 BOM CSV', 'Drop BOM CSV'],
  'quote.bomHint': ['可选 · 自动读取料号、贴片点和单价', 'Optional · reads parts, placements and price'],
  'quote.bomRemove': ['移除', 'Remove'],
  'quote.parts': ['料号', 'Parts'],
  'quote.placements': ['贴片点', 'Placements'],
  'quote.components': ['元件/片', 'Parts/pc'],
  'quote.total': ['合计', 'Total'],
  'quote.perPiece': ['每片', 'per pc'],
  'quote.leadTime': ['交期', 'Lead'],
  'quote.area': ['板面积', 'Area'],
  'quote.breakdown': ['明细', 'Breakdown'],
  'quote.hideBreakdown': ['收起明细', 'Hide breakdown'],
  'quote.disclaimer': [
    '仅为指示价。最终价格以 DFM 与工程评审为准，不含运费和税费。',
    'Indicative only. Final price after DFM and engineering review. Shipping and taxes excluded.',
  ],
  'quote.reset': ['恢复默认', 'Reset'],
  'quote.version': ['价格版本 {v}', 'Rate card {v}'],
  'quote.zero': ['请上传 BOM 或填写点数', 'Add a BOM or placement count'],
  'quote.pcs': ['片', 'pcs'],
  'quote.auto': ['已从 Gerber 填充', 'Filled from Gerber'],

  // Submit
  'submit.open': ['发送询价', 'Send RFQ'],
  'submit.title': ['发送询价', 'Send RFQ'],
  'submit.sub': ['发送报价摘要和文件清单。Gerber 内容不会上传。', 'Sends the quote summary and file list. Gerber contents are not uploaded.'],
  'submit.name': ['姓名', 'Name'],
  'submit.email': ['工作邮箱', 'Work email'],
  'submit.company': ['公司', 'Company'],
  'submit.message': ['说明', 'Notes'],
  'submit.messagePlaceholder': ['工艺要求、目标日期、数量说明…', 'Process notes, target date, volume…'],
  'submit.consent': ['我同意就这次询价与我联系。', 'I agree to be contacted about this quote.'],
  'submit.send': ['发送', 'Send'],
  'submit.sending': ['发送中…', 'Sending…'],
  'submit.cancel': ['取消', 'Cancel'],
  'submit.nameRequired': ['请填写姓名。', 'Enter your name.'],
  'submit.emailRequired': ['请填写有效邮箱。', 'Enter a valid email.'],
  'submit.consentRequired': ['请勾选同意。', 'Please tick the consent box.'],
  'submit.sent': ['已发送，编号 {id}', 'Sent. Reference {id}'],
  'submit.copied': ['摘要已复制，粘贴到邮件或微信发给工程师即可。', 'Summary copied. Paste it into an email or chat.'],
  'submit.copyFailed': ['复制失败，请手动选择摘要。', 'Copy failed. Select the summary manually.'],
  'submit.offlineHint': ['试用版未连接服务器：提交会复制报价摘要。', 'Trial build has no server: submitting copies the summary.'],
  'submit.summary': ['报价摘要', 'Quote summary'],
  'rfq.contact': ['联系人', 'Contact'],
  'rfq.message': ['留言', 'Notes'],
  'rfq.disclaimer': ['仅为指示价。最终价格以 DFM 和工程评审为准。', 'Indicative estimate only. Final price subject to DFM and engineering review.'],
  'rfq.board': ['板子', 'Board'],
  'rfq.mode': ['模式', 'Mode'],
  'rfq.estimate': ['估价', 'Estimate'],
  'rfq.rateCard': ['报价版本', 'Rate card'],

  // Common
  'common.close': ['关闭', 'Close'],
  'common.cancel': ['取消', 'Cancel'],
  'common.delete': ['删除', 'Delete'],
  'common.save': ['保存', 'Save'],
  'common.retry': ['重试', 'Retry'],
  'common.on': ['开', 'On'],
  'common.off': ['关', 'Off'],
  'common.add': ['添加', 'Add'],
  'common.copy': ['复制', 'Copy'],
  'common.back': ['返回', 'Back'],

  // Toasts
  'toast.saved': ['已保存到本机', 'Saved on this device'],
  'toast.storageFull': ['本地存储空间不足。删除旧项目后重试。', 'Local storage is full. Delete an old project and try again.'],
  'toast.savedFailed': ['项目未能保存', 'Project could not be saved'],
  'toast.projectDeleted': ['项目已删除', 'Project deleted'],
  'toast.svgDownloaded': ['SVG 已下载', 'SVG downloaded'],
  'toast.bomLoaded': ['{name}：{parts} 个料号，{points} 个贴片点', '{name}: {parts} parts, {points} placements'],
  'toast.bomOnly': ['请先打开一块板，再添加 BOM', 'Open a board before adding a BOM'],
  'toast.badFile': ['无法读取 {name}', 'Could not read {name}'],
  'toast.limit': ['文件超过 {mb} MB', 'File exceeds {mb} MB'],
  'toast.demoFailed': ['示例板加载失败', 'Demo board could not be loaded'],
  'toast.noFiles': ['没有找到 Gerber 文件', 'No Gerber files found'],
  'toast.renamed': ['已重命名', 'Renamed'],
  'toast.imported': ['已识别并导入 {n} 个项目', 'Imported {n} projects'],
  'toast.importSkipped': ['已导入 {n} 个，跳过 {skipped} 个', 'Imported {n}, skipped {skipped}'],
  'toast.importLimited': ['项目较多，先导入前 {n} 个', 'Many projects found; imported the first {n}'],
  'toast.fit': ['已适应画布', 'Fit to view'],

  // Errors
  'error.tooLarge': ['上传文件超过 {mb} MB。', 'Upload is larger than {mb} MB.'],
  'error.folderTooLarge': ['文件夹超过 {mb} MB，请分几次选择。', 'Folder exceeds {mb} MB. Select it in parts.'],
  'error.folderTooMany': ['文件太多（{n} 个），请分几次选择。', 'Too many files ({n}). Select them in parts.'],
  'error.invalidZip': ['无法读取 {name}。它是有效的 ZIP 吗？', 'Could not read {name}. Is it a valid ZIP?'],
  'error.noGerber': ['没有找到 Gerber 或钻孔文件。请上传包含 Gerber / Excellon 的 ZIP。', 'No Gerber or drill files found. Upload a ZIP containing Gerber / Excellon files.'],
  'error.renderer': ['Gerber 渲染器加载失败。请检查网络后重试。', 'Gerber renderer failed to load. Check your connection and retry.'],
  'error.noMapped': ['没有可渲染的图层，请先映射文件。', 'No renderable layers. Map the files first.'],
  'error.geometry': ['无法从这些文件读取板子尺寸。', 'Could not read board geometry from these files.'],
  'error.bomEmpty': ['BOM 是空的。', 'The BOM is empty.'],
  'error.bomDesignator': ['BOM 里没有位号或数量列。', 'The BOM has no designator or quantity column.'],
  'error.bomCsv': ['请上传 CSV 格式的 BOM。', 'Upload the BOM as CSV.'],
  'error.bomTooLarge': ['BOM 文件过大。', 'The BOM file is too large.'],
  'error.unknown': ['出了点问题，请重试。', 'Something went wrong. Try again.'],
  'warn.unmapped': ['{n} 个文件未识别', '{n} unmapped'],
  'warn.panel': ['拼板：显示整块板', 'Panel: whole panel shown'],
  'warn.noOutline': ['缺板框', 'No outline'],
  'warn.noDrill': ['缺钻孔文件', 'No drill file'],
  'bom.warnNoMpn': ['BOM 没有 MPN 列，料号按位号 / 描述去重。', 'No MPN column. Parts are keyed by designator / description.'],
  'bom.warnNoPrice': ['BOM 没有单价列，元件成本未计入。', 'No unit-price column. Component cost is excluded.'],
  'bom.warnPartialPrice': ['部分行没有单价，元件成本只含已标价的行。', 'Some rows have no price. Component cost covers priced rows only.'],
  'bom.warnNoPlacements': ['没有读到贴片点数，请检查数量列。', 'No placements found. Check the quantity column.'],

  // Legal
  'legal.title': ['隐私与条款', 'Privacy & terms'],
  'legal.privacy': ['隐私', 'Privacy'],
  'legal.terms': ['条款', 'Terms'],
  'legal.files': ['文件处理', 'File handling'],

  // Command palette
  'palette.placeholder': ['搜索项目或操作…', 'Search projects or actions…'],
  'palette.projects': ['项目', 'Projects'],
  'palette.actions': ['操作', 'Actions'],
  'palette.upload': ['新建项目', 'New project'],
  'palette.folder': ['导入文件夹', 'Import folder'],
  'palette.demo': ['打开示例板', 'Open demo board'],
  'palette.fit': ['适应画布', 'Fit to view'],
  'palette.download': ['下载 SVG', 'Download SVG'],
  'palette.lang': ['切换到英文', 'Switch to Chinese'],
  'palette.delete': ['删除当前项目', 'Delete current project'],
  'palette.noResults': ['没有结果', 'No results'],
  'palette.hint': ['打开命令', 'Command menu'],
  'palette.shortcut': ['⌘K', '⌘K'],
  'palette.esc': ['ESC', 'ESC'],

  // Finish / options
  'finish.hasl': ['HASL', 'HASL'],
  'finish.hasl_lf': ['无铅 HASL', 'Lead-free HASL'],
  'finish.osp': ['OSP', 'OSP'],
  'finish.enig': ['ENIG', 'ENIG'],
  'finish.imm_silver': ['沉银', 'Immersion silver'],
  'finish.imm_tin': ['沉锡', 'Immersion tin'],
  'finish.hard_gold': ['硬金', 'Hard gold'],
  'trace.6': ['6/6 mil', '6/6 mil'],
  'trace.5': ['5/5 mil', '5/5 mil'],
  'trace.4': ['4/4 mil', '4/4 mil'],
  'trace.3': ['3/3 mil', '3/3 mil'],
  'trace.2': ['2/2 mil', '2/2 mil'],
  'mask.green': ['绿色', 'Green'],
  'mask.black': ['黑色', 'Black'],
  'mask.matte_black': ['哑黑', 'Matte black'],
  'mask.blue': ['蓝色', 'Blue'],
  'mask.red': ['红色', 'Red'],
  'mask.white': ['白色', 'White'],
  'silkscreen.white': ['白色', 'White'],
  'silkscreen.black': ['黑色', 'Black'],
  'silkscreen.none': ['无', 'None'],
  'lead.pcb.standard': ['5–7 个工作日', '5–7 business days'],
  'lead.pcb.express': ['3–4 个工作日', '3–4 business days'],
  'lead.pcb.rush': ['48 小时', '48 hours'],
  'lead.pcb.24h': ['24 小时', '24 hours'],
  'lead.pcba.standard': ['10–15 个工作日', '10–15 business days'],
  'lead.pcba.express': ['6–8 个工作日', '6–8 business days'],
  'lead.pcba.rush': ['4–5 个工作日', '4–5 business days'],
  'lead.pcba.24h': ['不可用——按特急报价', 'Not available — quoted as rush'],
  'tech.smt': ['SMT', 'SMT'],
  'tech.tht': ['插件', 'Through-hole'],
  'tech.mixed': ['混装', 'Mixed'],
  'complexity.standard': ['标准', 'Standard'],
  'complexity.fine_pitch': ['细间距', 'Fine pitch'],
  'complexity.bga': ['BGA', 'BGA'],
  'sides.top': ['单面', 'Top only'],
  'sides.both': ['双面', 'Both sides'],
  'test.aoi': ['AOI', 'AOI'],
  'test.xray': ['AOI + X-ray', 'AOI + X-ray'],
  'test.functional': ['AOI + 功能', 'AOI + functional'],
  'testing.e_test': ['电测（含）', 'E-test (included)'],
  'testing.flying_probe': ['飞针', 'Flying probe'],
  'testing.fixture': ['测试架', 'Fixture'],
  'coating.none': ['无', 'None'],
  'coating.acrylic': ['丙烯酸', 'Acrylic'],
  'coating.silicone': ['硅胶', 'Silicone'],
  'qty.unit': ['片', 'pcs'],

  // Quote lines / warnings
  'quote.line.fabrication': ['制板 · {qty} 片', 'Fabrication · {qty} pcs'],
  'quote.line.setup': ['开机 / NRE', 'Setup / NRE'],
  'quote.line.electricalTest': ['电测', 'Electrical test'],
  'quote.line.expedite': ['加急', 'Expedite'],
  'quote.line.minOrder': ['最低订单调整', 'Minimum order adjustment'],
  'quote.line.total': ['合计', 'Total'],
  'quote.line.barePcb': ['裸板制板', 'Bare PCB fabrication'],
  'quote.line.assemblySetup': ['贴片开机 / 钢网', 'Assembly setup / stencil'],
  'quote.line.assembly': ['贴片 · {qty} 点', 'Assembly · {qty} placements'],
  'quote.line.assemblyTest': ['贴片测试', 'Assembly test'],
  'quote.line.coating': ['三防漆', 'Conformal coating'],
  'quote.line.box': ['整机组装', 'Box build'],
  'quote.line.components': ['元件（来自 BOM）', 'Components (from BOM)'],
  'quote.warn.layers': ['超过 8 层需要工程评审。', 'Above 8 layers needs engineering review.'],
  'quote.warn.fineLine': ['细线价格需 DFM 确认。', 'Fine-line pricing needs a DFM check.'],
  'quote.warn.hardGold': ['硬金价格取决于金厚。', 'Hard gold price depends on thickness.'],
  'quote.warn.impedance': ['阻抗控制需先评审叠层。', 'Impedance control needs a stackup review.'],
  'quote.warn.componentsExcluded': ['未含元件成本，请确认采购价。', 'Component cost excluded. Confirm sourcing.'],
  'quote.warn.noBom': ['未上传 BOM，贴片点为估算。', 'No BOM. Placements are an estimate.'],
  'quote.warn.24h': ['PCBA 不支持 24 小时交期，按特急计价。', '24-hour PCBA is unavailable; rush pricing applied.'],
  'quote.warn.zeroPlacements': ['贴片点数为 0。', 'Zero placements.'],

  // Layer type names
  'layer.type.copper': ['铜层', 'copper'],
  'layer.type.soldermask': ['阻焊', 'soldermask'],
  'layer.type.silkscreen': ['丝印', 'silkscreen'],
  'layer.type.solderpaste': ['锡膏', 'solderpaste'],
  'layer.type.drill': ['钻孔', 'drill'],
  'layer.type.outline': ['外形', 'outline'],
  'layer.type.drawing': ['绘图', 'drawing'],
  'layer.type.unmapped': ['未识别', 'unmapped'],
  'layer.side.top': ['顶层', 'top'],
  'layer.side.bottom': ['底层', 'bottom'],
  'layer.side.inner': ['内层', 'inner'],
  'layer.side.all': ['全部', 'all'],
}

let current: Lang = detectInitialLang()
const listeners = new Set<() => void>()

function detectInitialLang(): Lang {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('lang')
    if (fromUrl === 'zh' || fromUrl === 'en') return fromUrl
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'zh' || stored === 'en') return stored
  } catch {
    // Private mode / blocked storage: fall through to Chinese.
  }
  return 'zh'
}

function applyDocumentLang(lang: Lang): void {
  document.documentElement.lang = lang === 'zh' ? 'zh-CN' : 'en'
  document.title = dict['app.docTitle'][lang === 'zh' ? 0 : 1]
  document.querySelector('meta[name="description"]')?.setAttribute('content', dict['app.description'][lang === 'zh' ? 0 : 1])
  document.querySelector('meta[property="og:title"]')?.setAttribute('content', document.title)
  document.querySelector('meta[property="og:description"]')?.setAttribute('content', dict['app.description'][lang === 'zh' ? 0 : 1])
}

applyDocumentLang(current)

export function getLang(): Lang {
  return current
}

export function setLang(next: Lang): void {
  if (next === current) return
  current = next
  try {
    localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Storage can be unavailable; the in-memory choice still applies.
  }
  applyDocumentLang(next)
  for (const listener of listeners) listener()
}

export function subscribeLang(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useLang(): Lang {
  return useSyncExternalStore(subscribeLang, getLang, getLang)
}

export function locale(): string {
  return current === 'zh' ? 'zh-CN' : 'en-US'
}

export function t(key: string, vars?: Record<string, string | number>): string {
  const entry = dict[key]
  let out = entry ? entry[current === 'zh' ? 0 : 1] : key
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      out = out.split(`{${name}}`).join(String(value))
    }
  }
  return out
}

/** Auto-named boards from ZIP/file names, kept short. */
export function boardNameFromFile(name: string): string {
  const raw = name
    .replace(/\.zip$/i, '')
    .replace(/[-_ ]*(gerber|files?|output|production)[-_ ]*/gi, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 48)
  if (!raw) return t('project.untitled')
  if (/^[a-z]/.test(raw)) return raw.charAt(0).toUpperCase() + raw.slice(1)
  return raw
}
